/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';
import { DebtModel } from 'src/types/debt.model';
import MixpanelService from '../services/mixpanel.service';
import DateUtils from 'src/utils/date';
import BWDataService from './bw-data.service';

export interface PayDownData {
    debts: Array<{
        amount: number;
        id: number;
        interest: number;
        name: string;
        payoffDate: string;
    }>;
    estimatedPayoffDate: string;
    estimatedSavings: number;
    totalInterestPaid: number;
}

export default class DebtService extends BWDataService {
    public static async createDebt(debtToBeCreated: DebtModel): Promise<DebtModel> {
        return await this.postDebt(debtToBeCreated);
    }

    private static async postDebt(debt: DebtModel): Promise<DebtModel> {
        const payload: DebtModel = {
            amount: debt.amount,
            interest: debt.interest,
            startDate: debt.startDate,
            endDate: debt.endDate,
            name: debt.name,
            type: debt.type,
            subtype: debt.subtype,
            duration: debt.duration,
            userSpecifiedMonthlyPayment: debt.userSpecifiedMonthlyPayment
        };

        const newDebt = await this.post('/debts', payload);
        newDebt.startDate = DateUtils.dateify(debt.startDate);
        newDebt.endDate = DateUtils.dateify(debt.endDate);

        MixpanelService.track('Add Debt', {
            Type: debt.type
        });

        return newDebt;
    }

    public static async getDebts(): Promise<DebtModel[]> {
        const debts: DebtModel[] = await this.get('/debts');
        debts?.forEach((debt) => {
            debt.startDate = DateUtils.dateify(debt.startDate);
            debt.endDate = DateUtils.dateify(debt.endDate);
        });
        return debts;
    }

    public static async getPayDownData(startDate: string, amount: number): Promise<PayDownData> {
        return await this.get(`/calculator/payDownDebt?startDate=${startDate}&paymentAmount=${amount}`);
    }

    public static async deleteDebt(debt: DebtModel): Promise<number> {
        return await this.delete(`/debts/${debt.id}`);
    }

    public static async updateDebt(debt: DebtModel): Promise<DebtModel> {
        const payload: DebtModel = {
            amount: debt.amount,
            interest: debt.interest,
            startDate: debt.startDate,
            endDate: debt.endDate,
            name: debt.name,
            type: debt.type,
            subtype: debt.subtype,
            duration: debt.duration
        };
        const changedDebt = await this.put(`/debts/${debt.id}`, payload);
        changedDebt.startDate = DateUtils.dateify(changedDebt.startDate);
        changedDebt.endDate = DateUtils.dateify(changedDebt.endDate);
        return changedDebt;
    }

    /** Create debt from formik values */
    public static async createDebtFromFormik(values: any, type?: string, subtype?: string): Promise<void> {
        const debt = {
            type,
            subtype,
            name: values.name,
            startDate: values.startDate || LocalDate.now(),
            amount: values.amount,
            interest: values.interest,
            duration: values.duration
        } as DebtModel;
        await DebtService.createDebt(debt);
    }

    /** Update debt from formik values */
    public static async updateDebtFromFormik(formValues: any, debt: DebtModel): Promise<void> {
        await DebtService.updateDebt({
            ...debt,
            ...formValues
        });
    }
}
